import { translations } from './translations.js';

/**
 * Translate a key to the specified language
 * Simple, fast, zero dependencies
 * 
 * @param {string} key - Translation key (e.g., 'greeting', 'btn_wifiHelp')
 * @param {string} lang - Language code ('en', 'es', 'pt')
 * @param {object} variables - Optional variables for interpolation {name: 'value'}
 * @returns {string} Translated text
 * 
 * @example
 * t('greeting', 'en') // "Hi! I'm your Compudopt assistant..."
 * t('btn_wifiHelp', 'es') // "📶 Ayuda WiFi"
 * t('welcome', 'pt', {name: 'João'}) // "Olá João! Sou seu..."
 */
export function t(key, lang = 'en', variables = {}) {
  // Get translation, fallback to English, then key itself
  const text = translations[lang]?.[key] || translations.en[key] || key;
  
  // Simple variable interpolation: "Hello {name}" with {name: 'John'}.
  // split/join, not regex replace: a raw value replacement string would
  // expand $-patterns ($&, $') and a regex-special key would throw
  return Object.entries(variables).reduce(
    (str, [k, v]) => str.split(`{${k}}`).join(v),
    text
  );
}

/**
 * Get all button translations with btn_ prefix
 * Useful for batch operations
 * 
 * @param {string} lang - Language code
 * @returns {object} Object with button IDs and labels
 * 
 * @example
 * getButtonTranslations('en')
 * // { deviceSupport: "⚡ Device support", wifiHelp: "📶 WiFi Help", ... }
 */
export function getButtonTranslations(lang = 'en') {
  const langTranslations = translations[lang] || translations.en;
  const buttons = {};
  
  Object.entries(langTranslations).forEach(([key, value]) => {
    if (key.startsWith('btn_')) {
      const buttonId = key.replace('btn_', '');
      buttons[buttonId] = value;
    }
  });
  
  return buttons;
}

/**
 * Get all available language codes
 * 
 * @returns {string[]} Array of language codes
 */
export function getAvailableLanguages() {
  return Object.keys(translations);
}

/**
 * Check if a language is supported
 * 
 * @param {string} lang - Language code to check
 * @returns {boolean} True if language is supported
 */
export function isLanguageSupported(lang) {
  return lang in translations;
}

/**
 * Validate that all languages have the same keys (dev mode only)
 * Helps catch missing translations during development
 */
export function validateTranslations() {
  // Skip validation in production (browser-safe check)
  if (typeof window !== 'undefined' && 
      !window.location.hostname.includes('localhost') &&
      !window.location.search.includes('debug=true')) {
    return true;
  }
  
  const langs = Object.keys(translations);
  const enKeys = new Set(Object.keys(translations.en));
  let hasIssues = false;
  
  langs.forEach(lang => {
    if (lang === 'en') return;
    
    const langKeys = new Set(Object.keys(translations[lang]));
    
    // Missing keys
    const missing = [...enKeys].filter(k => !langKeys.has(k));
    if (missing.length) {
      console.warn(`[i18n] Missing ${lang} translations:`, missing);
      hasIssues = true;
    }
    
    // Extra keys
    const extra = [...langKeys].filter(k => !enKeys.has(k));
    if (extra.length) {
      console.warn(`[i18n] Extra ${lang} keys (not in EN):`, extra);
      hasIssues = true;
    }
  });
  
  if (!hasIssues) {
    console.log('[i18n] ✓ Translation validation passed - all languages have matching keys');
  }
  
  return !hasIssues;
}

// Auto-validate on import in dev (browser-safe check)
if (typeof window !== 'undefined' && 
    (window.location.hostname.includes('localhost') || 
     window.location.search.includes('debug=true'))) {
  validateTranslations();
}

